import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { customerFetchPartnerBtwApplicationById } from "../../core/axios/endpoints";
import BtwStatusPill from "../../components/btw-status-pill";
import { batteryUsageLabel, genderLabel } from "../../core/btw-field-labels";

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");
const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";
const fmtEUR = (n) =>
  `€ ${Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Jira-lozenges: neutraal / info / succes / gevaar (designbrief).
const INVOICE_STATUS_LABELS = {
  pending: { label: "Nog te factureren", color: "bg-[#091E420F] text-ink-subtle" },
  invoiced: { label: "Gefactureerd", color: "bg-brand-subtle text-brand" },
  paid: { label: "Factuur betaald", color: "bg-[#DCFFF1] text-[#1F845A]" },
  credited: { label: "Gecrediteerd", color: "bg-[#FFECEB] text-[#AE2A19]" },
};

const Pill = ({ map, value }) => {
  const cfg = map[value] ?? { label: value || "-", color: "bg-[#091E420F] text-ink-subtle" };
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

const Section = ({ title, children }) => (
  <div className="rounded bg-white p-5 shadow-card">
    <h3 className="mb-3 text-base font-semibold text-ink">
      {title}
    </h3>
    <dl className="space-y-2">{children}</dl>
  </div>
);

const Row = ({ label, value, mono = false, masked = false }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
    <dt className="text-ink-subtlest">{label}</dt>
    <dd
      className={`text-right text-ink ${mono ? "text-xs font-medium text-ink-subtlest" : ""} ${
        masked ? "text-ink-disabled" : ""
      }`}
    >
      {value || "-"}
    </dd>
  </div>
);

const PartnerBtwTeruggaveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    customerFetchPartnerBtwApplicationById(id)
      .then((res) => {
        if (cancelled) return;
        setData(res?.data ?? res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.status === 404
            ? "Deze aanvraag is niet gevonden of u heeft er geen toegang toe."
            : e?.message ?? "Kon aanvraag niet laden.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content p-8 text-center text-sm text-ink-subtlest">Laden…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="page-wrapper">
        <div className="content p-8 text-center">
          <p className="text-sm text-[#AE2A19]">{error}</p>
          <Link
            to="/panel/customer/btw-teruggaves"
            className="mt-4 inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
          >
            Terug naar overzicht
          </Link>
        </div>
      </div>
    );
  }

  const step1 = data.step1 ?? {};
  const step3 = data.step3 ?? {};
  const fullName = `${step1.voorletters ?? ""} ${step1.tussenvoegsel ?? ""} ${step1.achternaam ?? ""}`
    .replace(/\s+/g, " ")
    .trim();

  return (
    <div className="page-wrapper">
      <div className="content space-y-4">
        {/* Header */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <nav className="mb-1 text-xs text-ink-subtlest">
              <button
                type="button"
                onClick={() => navigate("/panel/customer/btw-teruggaves")}
                className="hover:underline"
              >
                BTW Teruggaves
              </button>
              <span className="mx-1">/</span>
              <span>{fullName}</span>
            </nav>
            <h1 className="text-2xl font-medium text-ink">{fullName}</h1>
            <p className="mt-1 text-sm text-ink-subtlest">
              Aanvraag ingediend op {fmtDate(data.createdAt)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <BtwStatusPill status={data.workflowStatusId} />
            <Pill
              map={INVOICE_STATUS_LABELS}
              value={data.partnerInvoice?.status ?? "pending"}
            />
          </div>
        </header>

        {/* PII-info banner */}
        <div className="flex items-start gap-2 rounded bg-[#FFF7D6] px-3 py-2 text-sm text-[#7F5F01]">
          <FeatherIcon icon="lock" size={16} className="mt-0.5 shrink-0" />
          <p>
            <strong>Privacy:</strong> BSN, IBAN en geboortedatum worden ter
            bescherming van de klant gemaskeerd. De volledige gegevens zijn alleen
            inzichtelijk voor Subsidie2gether.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Klant */}
          <Section title="Klantgegevens">
            <Row label="Naam" value={fullName} />
            <Row label="Geslacht" value={genderLabel(step1.geslacht) || "-"} />
            <Row label="Email" value={step1.email} />
            <Row label="Telefoon" value={step1.telefoon} />
            <Row
              label="Adres"
              value={
                [step1.straatnaam, step1.huisnummer]
                  .filter(Boolean)
                  .join(" ") || "-"
              }
            />
            <Row
              label="Postcode + plaats"
              value={[step1.postcode, step1.plaatsnaam].filter(Boolean).join(", ")}
            />
          </Section>

          {/* Aanvraag-details */}
          <Section title="Aanvraag-details">
            <Row label="Geboortedatum" value={step3.geboortedatum} masked />
            <Row label="BSN" value={step3.bsn} masked mono />
            <Row label="IBAN" value={step3.iban} masked mono />
            <Row label="Tenaamstelling IBAN" value={step3.ibanHolder} />
            <Row label="Installatiedatum" value={fmtDate(step3.installationDate)} />
            <Row label="Factuurdatum" value={fmtDate(step3.invoiceDate)} />
            <Row
              label="Batterij capaciteit"
              value={
                step3.batteryCapacityKwh
                  ? `${step3.batteryCapacityKwh} kWh`
                  : "-"
              }
            />
            <Row label="Gebruik" value={batteryUsageLabel(step3.batteryUsage)} />
            <Row
              label="Dynamisch energiecontract"
              value={step3.dynamicEnergyContract}
            />
            <Row
              label="Aantal documenten"
              value={`${data.documentsCount ?? 0} bestand(en)`}
            />
          </Section>
        </div>

        {/* Factuur-info (alleen als gefactureerd EN populated, niet alleen ObjectId) */}
        {data.partnerInvoice?.invoiceId &&
        typeof data.partnerInvoice.invoiceId === "object" ? (
          <Section title="Factuur naar u">
            <Row
              label="Factuurnummer"
              value={data.partnerInvoice.invoiceId.invoiceNumber ?? "-"}
              mono
            />
            <Row
              label="Status"
              value={
                <Pill
                  map={INVOICE_STATUS_LABELS}
                  value={data.partnerInvoice.status ?? "pending"}
                />
              }
            />
            <Row
              label="Bedrag (incl. btw)"
              value={
                typeof data.partnerInvoice.invoiceId.totalIncl === "number"
                  ? fmtEUR(data.partnerInvoice.invoiceId.totalIncl)
                  : "-"
              }
            />
            <Row
              label="Factuurdatum"
              value={
                data.partnerInvoice.invoiceId.invoiceDate
                  ? fmtDate(data.partnerInvoice.invoiceId.invoiceDate)
                  : "-"
              }
            />
            {data.partnerInvoice.invoicedAt ? (
              <Row
                label="Gefactureerd op"
                value={fmtDate(data.partnerInvoice.invoicedAt)}
              />
            ) : null}
            {data.partnerInvoice.paidAt ? (
              <Row label="Betaald op" value={fmtDate(data.partnerInvoice.paidAt)} />
            ) : null}
          </Section>
        ) : null}

        {/* Voortgang (alleen partner-relevante events) */}
        {Array.isArray(data.activities) && data.activities.length > 0 ? (
          <Section title="Voortgang">
            <ul className="space-y-3">
              {data.activities.map((a) => {
                const dotColor =
                  a.variant === "paid"
                    ? "bg-[#1F845A]"
                    : a.variant === "invoiced"
                    ? "bg-brand"
                    : a.variant === "credited"
                    ? "bg-[#AE2A19]"
                    : a.variant === "status"
                    ? "bg-[#5E4DB2]"
                    : "bg-ink-disabled";
                return (
                  <li key={String(a._id)} className="flex items-start gap-3">
                    <div
                      className={`mt-1.5 size-2 flex-shrink-0 rounded-full ${dotColor}`}
                    />
                    <div className="flex-1 text-sm">
                      <p className="text-ink">{a.label}</p>
                      <p className="text-xs text-ink-subtlest">{fmtDateTime(a.createdAt)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Section>
        ) : null}
      </div>
    </div>
  );
};

export default PartnerBtwTeruggaveDetail;
